import { useContext, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../hooks/useTranslation';
import AgentKnowledgeStep from '../../forms/AgentKnowledgeStep/AgentKnowledgeStep';
import StyledForm from '../../../styles/StyledForm';
import Knowledge from '../../../interfaces/Knowledge';
import {
  buildWebscrapPayload,
  parseDigests,
  parseExcludeUrlPatterns,
  parseWebscrapLinks,
  parseWebscrapSourcePage,
  WEBSCRAP_DEFAULT_MAX_PAGES,
  WEBSCRAP_DEFAULT_PAGES_PER_PDF,
} from '../../../helpers/knowledgeHelper';
import {
  createKnowledge,
  isKnowledgeAttachedToAgent,
  updateKnowledge,
} from '../../../services/knowledgeService';
import {
  isWebscrapInProgress,
  saveWebscrapConfig,
  uploadKnowledgeFiles,
} from '../../../helpers/knowledgeHelper';
import AuthContext from '../../../contexes/AuthContext';
import useKnowledgeFile from '../../../hooks/useKnowledgeFile';
import { Alert, Box } from '@mui/material';
import StyledFormStep from '../../../styles/StyledFormStep';
import { ResponseObject, WorkflowType } from '../../../interfaces/App';
import ProcessingContainer from '../../shared/Processing/ProcessingContainer';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import Modal from '../../shared/Modal/Modal';
import WorkflowFooter from '../../shared/WorkflowFooter/WorkflowFooter';

interface KnowledgeFormProps {
  knowledge?: Knowledge;
  workflow?: WorkflowType;
}

const KnowledgeForm = ({
  knowledge,
  workflow = WorkflowType.CREATE,
}: KnowledgeFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const webscrapSource = parseWebscrapSourcePage(
    knowledge?.webscrap?.source_page,
    knowledge?.webscrap?.max_depth
  );
  const methods = useForm({
    defaultValues: {
      knowledge_name: workflow === WorkflowType.CREATE ? '' : knowledge?.name,
      knowledge_language:
        workflow === WorkflowType.CREATE ? 'en' : knowledge?.language,
      webscrap_enabled: !!knowledge?.webscrap?.enabled,
      webscrap_frequency: knowledge?.webscrap?.frequency ?? 'weekly',
      webscrap_source_url: webscrapSource.url,
      webscrap_use_sitemap: webscrapSource.useSitemap,
      webscrap_max_depth: webscrapSource.maxDepth,
      webscrap_max_pages:
        knowledge?.webscrap?.max_pages ?? WEBSCRAP_DEFAULT_MAX_PAGES,
      webscrap_pages_per_pdf:
        knowledge?.webscrap?.pages_per_pdf ?? WEBSCRAP_DEFAULT_PAGES_PER_PDF,
      webscrap_links: parseWebscrapLinks(knowledge?.webscrap?.links),
      webscrap_exclude_url_patterns: parseExcludeUrlPatterns(
        knowledge?.webscrap?.exclude_url_patterns
      ),
      webscrap_scrape_images: !!knowledge?.webscrap?.scrape_images,
      webscrap_digests: parseDigests(knowledge?.webscrap?.digests),
    },
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [editStartTime, setEditStartTime] = useState<number>(() => Date.now());

  // Track if the form is dirty
  const [isFormDirty, setIsFormDirty] = useState(false);
  // Monitor the form state
  useEffect(() => {
    setIsFormDirty(methods.formState.isDirty);
  }, [methods.formState.isDirty]);

  const [isProcessing, setIsProcessing] = useState(false); // reused for updating
  const [isKnowledgeCreated, setIsKnowledgeCreated] = useState(false); // reused for updating
  const [isFilesUploaded, setIsFilesUploaded] = useState(false); // reused for updating
  // Read by the completion effect below to decide which single toast to
  // show — doesn't need to be state since it never drives a render itself.
  const deletionFailuresRef = useRef<string[]>([]);
  const uploadFailuresRef = useRef<string[]>([]);
  const webscrapWarningRef = useRef<string | undefined>(undefined);

  const { track } = useAnalytics();

  // Track time spent on edit
  useEffect(() => {
    setEditStartTime(Date.now());
  }, []);

  useEffect(() => {
    if (currentStep === 0) {
      workflow === WorkflowType.CREATE
        ? track(AnalyticsEvents.KNOWLEDGE_CREATION_STARTED)
        : track(AnalyticsEvents.KNOWLEDGE_EDIT_STARTED);
    }

    return () => {
      const duration = Date.now() - editStartTime;
      track(AnalyticsEvents.KNOWLEDGE_EDIT_DURATION, {
        durationMs: duration,
        workflow,
      });
      if (!isKnowledgeCreated) {
        workflow === WorkflowType.CREATE
          ? track(AnalyticsEvents.KNOWLEDGE_CREATION_ABANDONED)
          : track(AnalyticsEvents.KNOWLEDGE_EDIT_ABANDONED);
      }
    };
  }, []);

  useEffect(() => {
    if (isKnowledgeCreated) {
      workflow === WorkflowType.CREATE
        ? track(AnalyticsEvents.KNOWLEDGE_CREATION_COMPLETED)
        : track(AnalyticsEvents.KNOWLEDGE_EDIT_COMPLETED);
    }
  }, [isKnowledgeCreated]);

  const {
    isKnowledgeFilesLoading,
    knowledgeFiles,
    knowledgeFilesData,
    pendingDeletions,
    executeStagedDeletions,
    markKnowledgeFileForDeletion,
    unmarkKnowledgeFileForDeletion,
    handleDownloadKnowledgeFile,
    setIsKnowledgeFilesLoading,
    setKnowledgeFilesData,
    clearIndexDBFiles,
    clearKnowledgeFilesData,
  } = useKnowledgeFile(knowledge as Knowledge, 'knowledge');

  const { currentUser } = useContext(AuthContext);

  const { data: associationResult } = useQuery({
    queryKey: ['knowledge-agent-association', knowledge?.id],
    queryFn: () => isKnowledgeAttachedToAgent(knowledge?.id ?? ''),
    enabled: workflow === WorkflowType.EDIT && !!knowledge?.id,
  });
  const attachmentCount =
    (associationResult?.message as { count?: number } | undefined)?.count ?? 0;

  const webscrapInProgress = isWebscrapInProgress(knowledge);

  const navigate = useNavigate();
  const displayMessage = useSnackBar();

  const handleFieldChange = (field: string, value: string) => {
    track(AnalyticsEvents.KNOWLEDGE_FORM_FIELD_UPDATED, { field, value });
  };

  const steps = [
    <AgentKnowledgeStep
      pendingDeletions={pendingDeletions}
      deleteKnowledgeFileCallback={markKnowledgeFileForDeletion}
      unmarkKnowledgeFileForDeletion={unmarkKnowledgeFileForDeletion}
      downloadKnowledgeFileCallback={handleDownloadKnowledgeFile}
      key={0}
      knowledgeFiles={knowledgeFiles}
      knowledgeFilesData={knowledgeFilesData}
      label={knowledge && t('Edit Agent Knowledge')}
      setKnowledgeFilesData={setKnowledgeFilesData}
      setIsKnowledgeFilesLoading={setIsKnowledgeFilesLoading}
      onFieldChange={handleFieldChange}
      isKnowledgeFilesLoading={isKnowledgeFilesLoading}
      disableFileControls={webscrapInProgress}
      showWebscrapConfig
      knowledgeId={workflow === WorkflowType.EDIT ? knowledge?.id : undefined}
    />,
  ];

  const handleValidationError = (errors: any) => {
    track(AnalyticsEvents.KNOWLEDGE_FORM_VALIDATION_ERROR, { errors });
  };

  const onSubmit = async (data: any) => {
    if (currentStep === steps.length - 1) {
      setIsProcessing(true);

      if (workflow === WorkflowType.CREATE) {
        setIsSubmitting(true);

        try {
          //
          // Create a knowledge
          //
          const dataToSubmit: Knowledge = {
            name: data.knowledge_name,
            user_id: currentUser!.id,
            company_id: currentUser!.company_id,
            language: data.knowledge_language,
          };

          const newKnowledgeResult: ResponseObject =
            await createKnowledge(dataToSubmit);
          if (newKnowledgeResult.status === 'success') {
            setIsKnowledgeCreated(true);

            // The create endpoint doesn't accept webscrap config directly
            // (it needs a knowledge id to attach to), so persist it with a
            // follow-up update once the knowledge exists. Non-fatal: the
            // knowledge itself was already created successfully, so the
            // warning goes into the completion toast.
            webscrapWarningRef.current = data.webscrap_enabled
              ? await saveWebscrapConfig(newKnowledgeResult.message.id, data, t)
              : undefined;

            // Settled (not `all`) so a failed knowledge-file upload can't
            // suppress the report of deletions that already completed.
            const [uploadResult, deletionResult] = await Promise.allSettled([
              uploadKnowledgeFiles(knowledgeFilesData, {
                id: newKnowledgeResult?.message?.id ?? '',
              } as Knowledge),
              executeStagedDeletions(),
            ]);

            uploadFailuresRef.current =
              uploadResult.status === 'fulfilled'
                ? uploadResult.value.failed
                : [];
            deletionFailuresRef.current =
              deletionResult.status === 'fulfilled'
                ? deletionResult.value.failed
                : [];
          } else {
            throw new Error(t('Knowledge creation failed.'));
          }

          setIsFilesUploaded(true);

          // Clear knowledge files data after successful upload
          clearKnowledgeFilesData();
        } catch (err: unknown) {
          if (err instanceof Error) {
            track(AnalyticsEvents.KNOWLEDGE_EDIT_FAILED, {
              error: err.message,
              workflow,
            });
            console.error(err.message);
            displayMessage(err.message, 'error', 6000);
            setIsProcessing(false);
          }
        } finally {
          setIsSubmitting(false);
        }
      }

      if (workflow === WorkflowType.EDIT) {
        //
        // Update an existing knowledge
        //
        setIsSubmitting(true);

        try {
          const dataToSubmit: Knowledge = {
            name: data.knowledge_name,
            language: data.knowledge_language,
            webscrap: buildWebscrapPayload(data),
          };

          const updateKnowledgeResult: ResponseObject = await updateKnowledge(
            knowledge?.id ?? '',
            dataToSubmit
          );
          if (updateKnowledgeResult?.status === 'success') {
            setIsKnowledgeCreated(true);

            // Settled (not `all`) so a failed knowledge-file upload can't
            // suppress the report of deletions that already completed.
            const [uploadResult, deletionResult] = await Promise.allSettled([
              uploadKnowledgeFiles(knowledgeFilesData, {
                id: knowledge?.id ?? '',
              } as Knowledge),
              executeStagedDeletions(),
            ]);

            uploadFailuresRef.current =
              uploadResult.status === 'fulfilled'
                ? uploadResult.value.failed
                : [];
            deletionFailuresRef.current =
              deletionResult.status === 'fulfilled'
                ? deletionResult.value.failed
                : [];
          } else {
            throw new Error(t('Knowledge update failed.'));
          }

          setIsFilesUploaded(true);

          // Clear knowledge files data after successful upload
          clearKnowledgeFilesData();
        } catch (err: unknown) {
          if (err instanceof Error) {
            track(AnalyticsEvents.KNOWLEDGE_EDIT_FAILED, {
              error: err.message,
              workflow,
            });
            console.error(err.message);
            displayMessage(err.message, 'error', 6000);
            setIsProcessing(false);
          }
        } finally {
          setIsSubmitting(false);
        }
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => setCurrentStep(prev => prev - 1);

  const navigateToKnowledges = () => {
    if (isFormDirty && !isKnowledgeCreated && !isFilesUploaded) {
      setShowLeaveConfirmation(true);
    } else {
      clearIndexDBFiles();
      // Invalidate all knowledge-related caches to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['knowledges'] });
      queryClient.invalidateQueries({
        queryKey: ['knowledges-with-attachment'],
      });
      navigate('/knowledges');
    }
  };

  const handleConfirmLeave = () => {
    setShowLeaveConfirmation(false);
    clearIndexDBFiles();
    // Invalidate all knowledge-related caches to refetch the updated list
    queryClient.invalidateQueries({ queryKey: ['knowledges'] });
    queryClient.invalidateQueries({ queryKey: ['knowledges-with-attachment'] });
    navigate('/knowledges');
  };

  const doneProcessing = () => {
    setIsProcessing(false);
    clearIndexDBFiles();
    // Remove all knowledge-related caches completely to force fresh fetch
    queryClient.removeQueries({ queryKey: ['knowledges'] });
    queryClient.removeQueries({ queryKey: ['knowledges-with-attachment'] });
    navigateToKnowledges();
  };

  // The snackbar only holds one message at a time, so webscrap, upload and
  // deletion problems are combined into a single message instead of clobbering each
  // other (or the generic success confirmation).
  const buildCompletionMessage = (
    successMessage: string
  ): { message: string; severity: 'success' | 'warning' } => {
    // Each part is a complete, independently-translated sentence (matching
    // the existing "Could not delete" key's style) - joined with a space
    // rather than spliced into one sentence, so translated output stays
    // grammatically valid in every locale instead of just in English.
    const parts: string[] = webscrapWarningRef.current
      ? [webscrapWarningRef.current]
      : [];
    if (uploadFailuresRef.current.length > 0) {
      parts.push(
        t('Could not upload: {{files}}', {
          files: uploadFailuresRef.current.join(', '),
        })
      );
    }
    if (deletionFailuresRef.current.length > 0) {
      parts.push(
        t('Could not delete: {{files}}', {
          files: deletionFailuresRef.current.join(', '),
        })
      );
    }
    if (parts.length === 0) {
      return { message: successMessage, severity: 'success' };
    }
    return {
      message: `${successMessage} ${parts.join(' ')}`,
      severity: 'warning',
    };
  };

  useEffect(() => {
    if (
      isKnowledgeCreated &&
      isFilesUploaded &&
      workflow === WorkflowType.CREATE
    ) {
      doneProcessing();
      const { message, severity } = buildCompletionMessage(
        t('New knowledge was created.')
      );
      if (severity === 'warning') {
        displayMessage(message, 'warning', 6000);
      } else {
        displayMessage(message, 'success');
      }
    }

    if (
      isKnowledgeCreated &&
      isFilesUploaded &&
      workflow === WorkflowType.EDIT
    ) {
      doneProcessing();
      const { message, severity } = buildCompletionMessage(
        t('Knowledge was updated.')
      );
      if (severity === 'warning') {
        displayMessage(message, 'warning', 6000);
      } else {
        displayMessage(message, 'success');
      }
    }
  }, [isKnowledgeCreated, isFilesUploaded]);

  // Add beforeunload handler
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isKnowledgeCreated) {
        e.preventDefault();
        return;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isKnowledgeCreated]);

  if (workflow === WorkflowType.CREATE && isProcessing) {
    return (
      <ProcessingContainer
        title={t('Knowledge Creation in Progress')}
        items={[
          {
            text: isKnowledgeCreated
              ? t('Knowledge Created')
              : t('Creating Knowledge...'),
            isCompleted: isKnowledgeCreated,
          },
          {
            text: isFilesUploaded
              ? t('Knowledge File(s) Uploaded')
              : t('Uploading Knowledge File(s)...'),
            isCompleted: isFilesUploaded,
          },
        ]}
      />
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={methods.handleSubmit(onSubmit, handleValidationError)}
          maxWidth="100%"
          margin="0"
        >
          {workflow === WorkflowType.EDIT && attachmentCount > 0 && (
            <Box sx={{ maxWidth: 1280, width: '500px', mx: 'auto', mb: 2 }}>
              <Alert severity="warning">
                {attachmentCount === 1
                  ? t('This knowledge base is used by 1 agent.')
                  : t('This knowledge base is used by {{count}} agents.', {
                      count: attachmentCount,
                    })}
              </Alert>
            </Box>
          )}
          <StyledFormStep>{steps[currentStep]}</StyledFormStep>
          <Box sx={{ maxWidth: 1280, width: '100%', mx: 'auto' }}>
            <WorkflowFooter
              currentStep={currentStep}
              isKnowledgeFilesLoading={isKnowledgeFilesLoading}
              isProcessing={isSubmitting}
              navigateToAgents={navigateToKnowledges}
              previousStep={prevStep}
              steps={steps}
              workflow={workflow}
            />
          </Box>
        </StyledForm>
      </FormProvider>

      <Modal
        mainPromptText={t(
          'Are you sure you want to leave? Your changes will be lost.'
        )}
        description={t(
          'You have unsaved changes. If you leave now, your changes will not be saved.'
        )}
        confirmText={t('Yes, leave')}
        denyText={t('No, stay')}
        modalShouldBeOpen={showLeaveConfirmation}
        setChangeStatusModalOpen={setShowLeaveConfirmation}
        callback={handleConfirmLeave}
        showButton={false}
      />
    </>
  );
};

export default KnowledgeForm;
